import React, { useEffect, useMemo, useState } from "react";
import {
	Card,
	Form,
	Button,
	FormGroup,
	FormControl,
	FormLabel,
	Container,
} from "react-bootstrap";
import { updateUser } from "../../services/userService";
import { getSports } from "../../services/series";
import { getEvents } from "../../services/eventService";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import { getUserThunk } from "../../store/thunks/authThunk";
import { Event, Series, Sport } from "../../types/model";
import { FantasySeries, FantasySports } from "../../constants/constants";
import {
	AVATAR_OPTIONS,
	DEFAULT_AVATAR_ID,
} from "../../constants/avatarOptions";
import UserAvatar from "../common/UserAvatar";
import "./UserProfile.css";

const EditUserProfile = () => {
	const { isGmail, user } = useAppSelector((state: RootState) => state.user);

	const dispatch = useAppDispatch();
	const [username, setUsername] = useState("");
	const [avatarId, setAvatarId] = useState(DEFAULT_AVATAR_ID);
	const [favoriteSportId, setFavoriteSportId] = useState("");
	const [favoriteSeriesId, setFavoriteSeriesId] = useState("");
	const [sportsList, setSportsList] = useState<Sport[]>([]);
	const [eventsList, setEventsList] = useState<Event[]>([]);
	const [oldPassword, setOldPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmNewPassword, setConfirmNewPassword] = useState("");
	// const [user, setUser] = useState(null);
	const toId = (value: unknown): string => {
		if (value === undefined || value === null) {
			return "";
		}
		if (typeof value === "object") {
			const maybeDoc = value as { _id?: unknown; id?: unknown; $oid?: unknown };
			if (maybeDoc._id !== undefined && maybeDoc._id !== null) {
				return String(maybeDoc._id);
			}
			if (maybeDoc.id !== undefined && maybeDoc.id !== null) {
				return String(maybeDoc.id);
			}
			if (maybeDoc.$oid !== undefined && maybeDoc.$oid !== null) {
				return String(maybeDoc.$oid);
			}
		}
		return String(value);
	};

	const handleUsernameChange = (e: any) => setUsername(e.target.value);
	const handleOldPasswordChange = (e: any) => setOldPassword(e.target.value);
	const handleNewPasswordChange = (e: any) => setNewPassword(e.target.value);

	useEffect(() => {
		fetchUser();
		fetchPreferenceData();
	}, []);

	useEffect(() => {
		if (user) {
			setUsername(user.username);
			setAvatarId(user.avatarId || DEFAULT_AVATAR_ID);
		}
	}, [user]);

	const seriesBySportId = useMemo(() => {
		const map = new Map<string, Series[]>();
		eventsList.forEach((event) => {
			const sportId = toId(event.sport?._id);
			const eventSeries = event.series;

			if (!sportId || !toId(eventSeries?._id)) {
				return;
			}

			const existingSeries = map.get(sportId) ?? [];
			if (
				!existingSeries.some(
					(seriesItem) => toId(seriesItem._id) === toId(eventSeries._id),
				)
			) {
				map.set(sportId, [...existingSeries, eventSeries]);
			}
		});
		return map;
	}, [eventsList]);

	const availableFavoriteSeries = useMemo(
		() => seriesBySportId.get(favoriteSportId) ?? [],
		[favoriteSportId, seriesBySportId],
	);

	useEffect(() => {
		if (!user || sportsList.length === 0) {
			return;
		}

		const userFavoriteSportId = toId(user.favoriteSportId);
		const userFavoriteSeriesId = toId(user.favoriteSeriesId);

		const defaultSport =
			sportsList.find((sport) => sport.name === FantasySports.Downhill) ??
			sportsList[0];
		const hasValidFavoriteSport =
			!!userFavoriteSportId &&
			sportsList.some((sport) => toId(sport._id) === userFavoriteSportId);
		const nextFavoriteSportId = hasValidFavoriteSport
			? userFavoriteSportId
			: toId(defaultSport?._id);

		setFavoriteSportId(nextFavoriteSportId);

		const seriesForSport = seriesBySportId.get(nextFavoriteSportId) ?? [];
		const defaultSeriesForSport =
			seriesForSport.find(
				(seriesItem) => seriesItem.name === FantasySeries.WorldCup,
			) ?? seriesForSport[0];
		const hasValidFavoriteSeries =
			!!userFavoriteSeriesId &&
			seriesForSport.some(
				(seriesItem) => toId(seriesItem._id) === userFavoriteSeriesId,
			);
		const nextFavoriteSeriesId = hasValidFavoriteSeries
			? userFavoriteSeriesId
			: toId(defaultSeriesForSport?._id);

		setFavoriteSeriesId(nextFavoriteSeriesId);
	}, [seriesBySportId, sportsList, user]);

	const fetchUser = async () => {
		try {
			dispatch(getUserThunk());
		} catch (error) {
			console.error("Error fetching user:", error);
		}
	};

	const fetchPreferenceData = async () => {
		try {
			const [sportsResponse, eventsResponse] = await Promise.all([
				getSports(),
				getEvents(),
			]);
			setSportsList(sportsResponse?.data ?? []);
			setEventsList(eventsResponse?.data ?? []);
		} catch (error) {
			console.error("Error fetching favorite sport/series options:", error);
		}
	};

	const handleConfirmNewPasswordChange = (e: any) =>
		setConfirmNewPassword(e.target.value);

	const handleUpdatePassword = async () => {
		if (newPassword !== confirmNewPassword) {
			alert("Passwords do not match");
			return;
		}

		const userData = {
			password: newPassword,
		};

		try {
			const res = await updateUser(user._id, userData);
			if (res) {
				fetchUser();
			}
		} catch (error) {
			console.error("Error updating password:", error);
		}
	};

	const handleSaveProfile = async () => {
		const userData = {
			username: username,
			avatarId: avatarId,
			favoriteSportId: favoriteSportId || undefined,
			favoriteSeriesId: favoriteSeriesId || undefined,
		};

		try {
			const res = await updateUser(user._id, userData);
			if (res) {
				fetchUser();
			}
		} catch (error) {
			alert("Error updating profile:" + error);
		}
	};

	const handleFavoriteSportChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const nextSportId = toId(e.target.value);
		setFavoriteSportId(nextSportId);

		const seriesForSport = seriesBySportId.get(nextSportId) ?? [];
		const keepCurrentSeries = seriesForSport.some(
			(seriesItem) => toId(seriesItem._id) === toId(favoriteSeriesId),
		);
		if (keepCurrentSeries) {
			return;
		}

		const defaultSeries =
			seriesForSport.find(
				(seriesItem) => seriesItem.name === FantasySeries.WorldCup,
			) ?? seriesForSport[0];
		setFavoriteSeriesId(toId(defaultSeries?._id));
	};

	const handleFavoriteSeriesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setFavoriteSeriesId(toId(e.target.value));
	};

	return (
		<Container className="py-4" style={{ minHeight: "100vh" }}>
			<h1 className="text-start fw-bold text-info mx-md-4">USER PROFILE</h1>
			<Card className="mx-md-4 my-4 bg-secondary text-light border-1 border-info">
				<Card.Body>
					<Form>
						{/* Username Field */}
						<FormGroup className="mb-3 text-light">
							<FormLabel>USERNAME</FormLabel>
							<Form.Control
								type="text"
								placeholder="Enter new username"
								className="text-light bg-dark custom-input custom-placeholder"
								style={{ color: "white" }}
								value={username}
								onChange={handleUsernameChange}
							/>
						</FormGroup>
						<FormGroup className="mb-4 text-light">
							<FormLabel>AVATAR</FormLabel>
							<div className="user-profile__avatar-picker">
								{AVATAR_OPTIONS.map((avatarOption) => {
									const isSelected = avatarId === avatarOption.id;
									return (
										<button
											key={avatarOption.id}
											type="button"
											className={`user-profile__avatar-option ${isSelected ? "selected" : ""}`}
											onClick={() => setAvatarId(avatarOption.id)}
											aria-label={avatarOption.label}
										>
											<UserAvatar avatarId={avatarOption.id} size={52} />
										</button>
									);
								})}
							</div>
						</FormGroup>
						<FormGroup className="mb-3 text-light">
							<FormLabel>FAVORITE SPORT</FormLabel>
							<Form.Select
								value={favoriteSportId}
								onChange={handleFavoriteSportChange}
								className="text-light bg-dark custom-input custom-placeholder"
							>
								<option value="" disabled>
									Select sport
								</option>
								{sportsList.map((sportItem) => (
									<option key={sportItem._id} value={sportItem._id}>
										{sportItem.name}
									</option>
								))}
							</Form.Select>
						</FormGroup>
						<FormGroup className="mb-3 text-light">
							<FormLabel>FAVORITE SERIES</FormLabel>
							<Form.Select
								value={favoriteSeriesId}
								onChange={handleFavoriteSeriesChange}
								className="text-light bg-dark custom-input custom-placeholder"
								disabled={!favoriteSportId || availableFavoriteSeries.length === 0}
							>
								<option value="" disabled>
									Select series
								</option>
								{availableFavoriteSeries.map((seriesItem) => (
									<option key={seriesItem._id} value={seriesItem._id}>
										{seriesItem.name}
									</option>
								))}
							</Form.Select>
						</FormGroup>
							{/* Marketing Agreement Checkbox */}
							<FormGroup className="mb-3 text-light">
								<FormLabel>APP THEME</FormLabel>
								<Form.Control
									type="text"
									value="Dark Mode"
									readOnly
									className="text-light bg-dark custom-input custom-placeholder"
								/>
							</FormGroup>
						{/* Marketing Agreement Checkbox */}
						<FormGroup className="mb-3 text-light">
							<Form.Check
								type="checkbox"
								id="agreeToMarketing"
								label="I DO WANT to subscribe to marketing updates, competitions team updates & pro tips"
								checked={user?.agreeToMarketing || false}
								onChange={(e) => {
									const userData = { agreeToMarketing: e.target.checked };
									updateUser(user._id, userData)
										.then(() => fetchUser())
										.catch((error) =>
											alert("Error updating preferences: " + error),
										);
								}}
								className="text-light"
							/>
						</FormGroup>

						{/* Terms Agreement Checkbox */}
						<FormGroup className="mb-3 text-light">
							<Form.Check
								type="checkbox"
								id="agreeToTerms"
								label={
									<span>
										I agree to the{" "}
										<a
											href="/termsandconditions"
											className="text-info"
											onClick={(e) => {
												e.stopPropagation();
											}}
										>
											terms and conditions
										</a>
									</span>
								}
								checked={user?.agreeToTerms || false}
								onChange={(e) => {
									const userData = { agreeToTerms: e.target.checked };
									updateUser(user._id, userData)
										.then(() => fetchUser())
										.catch((error) =>
											alert("Error updating preferences: " + error),
										);
								}}
								className="text-light"
							/>
						</FormGroup>
						<div className="d-flex flex-row justify-content-md-end justify-content-center">
							<Button variant="primary w-auto" onClick={handleSaveProfile}>
								Save Changes
							</Button>
						</div>
					</Form>
				</Card.Body>
			</Card>

			{!isGmail && (
				<Card className="mx-md-4 my-4 bg-secondary text-light border-1 border-info">
					<Card.Body>
						<Form>
							<>
								{/* Old Password Field */}
								<FormGroup className="mb-3 text-light">
									<FormLabel>OLD PASSWORD</FormLabel>
									<FormControl
										type="password"
										placeholder="Enter old password"
										className="text-light bg-dark custom-input custom-placeholder"
										value={oldPassword}
										onChange={handleOldPasswordChange}
									/>
								</FormGroup>

								{/* New Password Field */}
								<FormGroup className="mb-3 text-light">
									<FormLabel>NEW PASSWORD</FormLabel>
									<FormControl
										type="password"
										placeholder="Enter new password"
										className="text-light bg-dark custom-input custom-placeholder"
										value={newPassword}
										onChange={handleNewPasswordChange}
									/>
								</FormGroup>

								{/* Confirm New Password Field */}
								<FormGroup className="mb-3 text-light">
									<FormLabel>CONFIRM NEW PASSWORD</FormLabel>
									<FormControl
										type="password"
										placeholder="Confirm new password"
										className="text-light bg-dark custom-input custom-placeholder"
										value={confirmNewPassword}
										onChange={handleConfirmNewPasswordChange}
									/>
								</FormGroup>
							</>

							{/* Submit Button */}
							<div className="d-flex flex-row justify-content-md-end justify-content-center">
								<Button variant="primary w-auto" onClick={handleUpdatePassword}>
									Update Password
								</Button>
							</div>
						</Form>
					</Card.Body>
				</Card>
			)}
		</Container>
	);
};

export default EditUserProfile;
